import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import { styled } from '@mui/material/styles';
import { Calculator, Edit as EditIcon, Plus as PlusIcon, RefreshCw, Search as SearchIcon, Trash2 as DeleteIcon } from 'lucide-react';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  height: '100%'
}));

const toFixed2 = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return (Math.round(n * 100) / 100).toFixed(2);
};

const extractYmd = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  const ymd = str.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
  const date = new Date(str);
  if (!Number.isFinite(date.getTime())) return '';
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateIt = (value) => {
  const ymd = extractYmd(value);
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
};

const defaultForm = {
  data_inizio: '',
  data_fine: '',
  lunedi: '1.00',
  martedi: '1.00',
  mercoledi: '1.00',
  giovedi: '1.00',
  venerdi: '1.00',
  sabato: '1.00',
  domenica: '1.00',
  descrizione: ''
};

export default function KpiLeadRegoleCalcoloPage() {
  const { user } = useAuth();
  const roleName = (user?.role_name || user?.role || '').toLowerCase();
  const isAdmin = roleName === 'admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderDirection, setOrderDirection] = useState('desc');
  const [orderBy, setOrderBy] = useState('data_inizio');
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [current, setCurrent] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [error, setError] = useState('');

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await api.get('/kpi-lead/regole-calcolo');
      setRows(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (err) {
      console.error('Error fetching regole calcolo:', err);
      setError('Errore nel caricamento delle regole');
      toast.error('Errore nel caricamento delle regole');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const createSortHandler = (property) => () => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredRows = useMemo(() => {
    const s = String(search || '').trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((row) => {
      const descr = String(row?.descrizione || '').toLowerCase();
      const startIt = formatDateIt(row?.data_inizio).toLowerCase();
      const endIt = formatDateIt(row?.data_fine).toLowerCase();
      const startIso = extractYmd(row?.data_inizio).toLowerCase();
      const endIso = extractYmd(row?.data_fine).toLowerCase();
      return (
        descr.includes(s) ||
        startIt.includes(s) ||
        endIt.includes(s) ||
        startIso.includes(s) ||
        endIso.includes(s)
      );
    });
  }, [rows, search]);

  const orderedRows = useMemo(() => {
    const multiplierKeys = new Set(['lunedi', 'martedi', 'mercoledi', 'giovedi', 'venerdi', 'sabato', 'domenica']);
    const dir = orderDirection === 'asc' ? 1 : -1;
    const key = orderBy;

    return [...filteredRows].sort((a, b) => {
      if (key === 'periodo') {
        const aStart = extractYmd(a?.data_inizio);
        const bStart = extractYmd(b?.data_inizio);
        if (aStart !== bStart) return aStart < bStart ? -dir : dir;
        const aEnd = extractYmd(a?.data_fine);
        const bEnd = extractYmd(b?.data_fine);
        if (aEnd !== bEnd) return aEnd < bEnd ? -dir : dir;
        return (Number(a?.id || 0) - Number(b?.id || 0)) * dir;
      }

      if (key === 'data_inizio' || key === 'data_fine') {
        const aVal = extractYmd(a?.[key]);
        const bVal = extractYmd(b?.[key]);
        if (aVal !== bVal) return aVal < bVal ? -dir : dir;
        return (Number(a?.id || 0) - Number(b?.id || 0)) * dir;
      }

      if (multiplierKeys.has(key)) {
        const aNum = Number(a?.[key] ?? 0);
        const bNum = Number(b?.[key] ?? 0);
        if (aNum !== bNum) return (aNum - bNum) * dir;
        return (Number(a?.id || 0) - Number(b?.id || 0)) * dir;
      }

      if (key === 'descrizione') {
        const aStr = String(a?.descrizione || '');
        const bStr = String(b?.descrizione || '');
        const cmp = aStr.localeCompare(bStr, 'it', { sensitivity: 'base' });
        if (cmp !== 0) return cmp * dir;
        return (Number(a?.id || 0) - Number(b?.id || 0)) * dir;
      }

      return (Number(a?.id || 0) - Number(b?.id || 0)) * dir;
    });
  }, [filteredRows, orderBy, orderDirection]);

  const total = orderedRows.length;
  const pageRows = useMemo(() => {
    const start = page * rowsPerPage;
    return orderedRows.slice(start, start + rowsPerPage);
  }, [orderedRows, page, rowsPerPage]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (row = null) => {
    if (!isAdmin) return;
    if (row) {
      setCurrent(row);
      setFormData({
        data_inizio: extractYmd(row.data_inizio) || '',
        data_fine: extractYmd(row.data_fine) || '',
        lunedi: toFixed2(row.lunedi),
        martedi: toFixed2(row.martedi),
        mercoledi: toFixed2(row.mercoledi),
        giovedi: toFixed2(row.giovedi),
        venerdi: toFixed2(row.venerdi),
        sabato: toFixed2(row.sabato),
        domenica: toFixed2(row.domenica),
        descrizione: row.descrizione || ''
      });
    } else {
      setCurrent(null);
      setFormData(defaultForm);
    }
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrent(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (!isAdmin) return;
      const payload = {
        ...formData,
        data_inizio: extractYmd(formData.data_inizio),
        data_fine: extractYmd(formData.data_fine),
        lunedi: formData.lunedi === '' ? null : Number(formData.lunedi),
        martedi: formData.martedi === '' ? null : Number(formData.martedi),
        mercoledi: formData.mercoledi === '' ? null : Number(formData.mercoledi),
        giovedi: formData.giovedi === '' ? null : Number(formData.giovedi),
        venerdi: formData.venerdi === '' ? null : Number(formData.venerdi),
        sabato: formData.sabato === '' ? null : Number(formData.sabato),
        domenica: formData.domenica === '' ? null : Number(formData.domenica)
      };

      if (current?.id) {
        await api.put(`/kpi-lead/regole-calcolo/${current.id}`, payload);
        toast.success('Regola aggiornata con successo');
      } else {
        await api.post('/kpi-lead/regole-calcolo', payload);
        toast.success('Regola creata con successo');
      }

      await fetchRows();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving regola:', err);
      const message = err.response?.data?.message || 'Errore nel salvataggio';
      setError(message);
      toast.error(message);
    }
  };

  const openDeleteDialog = (row) => {
    if (!isAdmin) return;
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      if (!isAdmin) return;
      setDeleteLoading(true);
      await api.delete(`/kpi-lead/regole-calcolo/${deleteTarget.id}`);
      toast.success('Regola eliminata');
      closeDeleteDialog();
      await fetchRows();
    } catch (err) {
      console.error('Error deleting regola:', err);
      toast.error('Errore durante l’eliminazione');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Calculator size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Regole Calcolo (Lead Giornalieri)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Periodi e moltiplicatori per giorno della settimana
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshCw size={18} />}
            onClick={fetchRows}
            disabled={loading}
          >
            Aggiorna
          </Button>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<PlusIcon size={18} />}
              onClick={() => handleOpenDialog(null)}
            >
              Nuova regola
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <StyledCard>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                placeholder="Cerca per periodo o descrizione..."
                variant="outlined"
                size="small"
                fullWidth
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon size={20} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'periodo'}
                      direction={orderBy === 'periodo' ? orderDirection : 'asc'}
                      onClick={createSortHandler('periodo')}
                    >
                      Periodo
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'descrizione'}
                      direction={orderBy === 'descrizione' ? orderDirection : 'asc'}
                      onClick={createSortHandler('descrizione')}
                    >
                      Descrizione
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'lunedi'}
                      direction={orderBy === 'lunedi' ? orderDirection : 'asc'}
                      onClick={createSortHandler('lunedi')}
                    >
                      Lun
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'martedi'}
                      direction={orderBy === 'martedi' ? orderDirection : 'asc'}
                      onClick={createSortHandler('martedi')}
                    >
                      Mar
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'mercoledi'}
                      direction={orderBy === 'mercoledi' ? orderDirection : 'asc'}
                      onClick={createSortHandler('mercoledi')}
                    >
                      Mer
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'giovedi'}
                      direction={orderBy === 'giovedi' ? orderDirection : 'asc'}
                      onClick={createSortHandler('giovedi')}
                    >
                      Gio
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'venerdi'}
                      direction={orderBy === 'venerdi' ? orderDirection : 'asc'}
                      onClick={createSortHandler('venerdi')}
                    >
                      Ven
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'sabato'}
                      direction={orderBy === 'sabato' ? orderDirection : 'asc'}
                      onClick={createSortHandler('sabato')}
                    >
                      Sab
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'domenica'}
                      direction={orderBy === 'domenica' ? orderDirection : 'asc'}
                      onClick={createSortHandler('domenica')}
                    >
                      Dom
                    </TableSortLabel>
                  </TableCell>
                  {isAdmin && <TableCell align="right">Azioni</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 10 : 9} align="center" sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : pageRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 10 : 9} align="center" sx={{ py: 3 }}>
                      Nessuna regola presente
                    </TableCell>
                  </TableRow>
                ) : (
                  pageRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateIt(row.data_inizio)} → {formatDateIt(row.data_fine)}
                      </TableCell>
                      <TableCell>{row.descrizione || ''}</TableCell>
                      <TableCell align="right">{toFixed2(row.lunedi)}</TableCell>
                      <TableCell align="right">{toFixed2(row.martedi)}</TableCell>
                      <TableCell align="right">{toFixed2(row.mercoledi)}</TableCell>
                      <TableCell align="right">{toFixed2(row.giovedi)}</TableCell>
                      <TableCell align="right">{toFixed2(row.venerdi)}</TableCell>
                      <TableCell align="right">{toFixed2(row.sabato)}</TableCell>
                      <TableCell align="right">{toFixed2(row.domenica)}</TableCell>
                      {isAdmin && (
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              color="primary"
                              title="Modifica"
                              onClick={() => handleOpenDialog(row)}
                            >
                              <EditIcon size={18} />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              title="Elimina"
                              onClick={() => openDeleteDialog(row)}
                            >
                              <DeleteIcon size={18} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Righe per pagina"
          />
        </CardContent>
      </StyledCard>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{current ? 'Modifica regola' : 'Nuova regola'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="data_inizio"
                  label="Data inizio"
                  type="date"
                  value={formData.data_inizio}
                  onChange={handleChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="data_fine"
                  label="Data fine"
                  type="date"
                  value={formData.data_fine}
                  onChange={handleChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="descrizione"
                  label="Descrizione"
                  value={formData.descrizione}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  name="lunedi"
                  label="Lunedì"
                  type="number"
                  inputProps={{ step: 0.01, min: 0, max: 99.99 }}
                  value={formData.lunedi}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  name="martedi"
                  label="Martedì"
                  type="number"
                  inputProps={{ step: 0.01, min: 0, max: 99.99 }}
                  value={formData.martedi}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  name="mercoledi"
                  label="Mercoledì"
                  type="number"
                  inputProps={{ step: 0.01, min: 0, max: 99.99 }}
                  value={formData.mercoledi}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  name="giovedi"
                  label="Giovedì"
                  type="number"
                  inputProps={{ step: 0.01, min: 0, max: 99.99 }}
                  value={formData.giovedi}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  name="venerdi"
                  label="Venerdì"
                  type="number"
                  inputProps={{ step: 0.01, min: 0, max: 99.99 }}
                  value={formData.venerdi}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  name="sabato"
                  label="Sabato"
                  type="number"
                  inputProps={{ step: 0.01, min: 0, max: 99.99 }}
                  value={formData.sabato}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  name="domenica"
                  label="Domenica"
                  type="number"
                  inputProps={{ step: 0.01, min: 0, max: 99.99 }}
                  value={formData.domenica}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseDialog}>
            Annulla
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!isAdmin}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Eliminare la regola per il periodo {formatDateIt(deleteTarget?.data_inizio)} → {formatDateIt(deleteTarget?.data_fine)}
            {deleteTarget?.descrizione ? ` (${deleteTarget.descrizione})` : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteLoading}>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
